import { useEffect } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';
import { Category } from 'src/generics/data/generic-category/data.types';
import useProductQueryParams from 'src/hooks/use-product-query-params';
import useDispensary from 'src/dispensary/hooks/use-dispensary';

type UseSetUpRelevanceAsInitialSortParams = {
  allowSortByRelevance: boolean;
  category: Category | null;
};

/**
 * @deprecated This hook is part of menu v1 implementation and will be skipped when menu v2 is enabled.
 * TODO: Remove this hook when menu v1 is fully deprecated.
 */
export function useSetUpRelevanceAsInitialSort({
  allowSortByRelevance,
  category,
}: UseSetUpRelevanceAsInitialSortParams): void {
  const { queryParams, setQueryParams, defaults } = useProductQueryParams();
  const { dispensary } = useDispensary();
  const flags = useFlags();
  const isMenuV2Enabled = flags['ecomm.admin.menu-v2.rollout'] ?? false;
  const relevanceSortAdminOptionEnabled = flags['growth.ecomm.relevance-sort-admin-option.rollout'] ?? false;
  const relevanceConfiguredForCategory =
    dispensary?.menuSortSettings?.find((setting) => setting.category === category)?.menuOrder === 'relevance';

  const isRelevanceSort = queryParams.sortby === 'relevance';
  const isDefaultSort = queryParams.sortby === 'default';

  useEffect(() => {
    // This is menu v1 logic, exit early if v2 is enabled
    if (isMenuV2Enabled) {
      return;
    }
    // When admin option is enabled, only use relevance if configured for category
    if (relevanceSortAdminOptionEnabled && allowSortByRelevance) {
      if (relevanceConfiguredForCategory && isDefaultSort) {
        setQueryParams({ sortby: 'relevance', weight: defaults.weight });
      }
      return;
    }

    // Original behavior when admin option is disabled
    if (!allowSortByRelevance && isRelevanceSort) {
      setQueryParams({ sortby: 'default', weight: defaults.weight });
      return;
    }

    if (allowSortByRelevance && isDefaultSort) {
      setQueryParams({ sortby: 'relevance', weight: defaults.weight });
      return;
    }

    if (allowSortByRelevance && !queryParams.sortby) {
      setQueryParams({ sortby: 'relevance', weight: defaults.weight });
    }
  }, [
    allowSortByRelevance,
    isRelevanceSort,
    isDefaultSort,
    queryParams.sortby,
    setQueryParams,
    defaults.weight,
    relevanceSortAdminOptionEnabled,
    relevanceConfiguredForCategory,
    isMenuV2Enabled,
  ]);
}
